import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Typography,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Header from "../components/header";
import Footer from "../components/footer";
import RoleSelectionDialog from "../components/RoleSelectionDialog";
import ReviewTicker from "../components/ReviewTicker";
import dividerImg from "../assets/divider.png";
import TestimonialCard from "../components/TestimonialCard";
import homePageBanner from "../assets/homePageBanner.png";
import mechanicGraphic from "../assets/homePageMechanicGraphic.png";
import biddingGraphic from "../assets/homePageBiddingGraphic.png";
import ShopifyCollection from "../components/ShopifyCollection";
import getApiBase from "../components/getApiBase";

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [shopifyEnabled, setShopifyEnabled] = useState(false);

  // Fetch Shopify feature status
  useEffect(() => {
    const fetchShopifyStatus = async () => {
      try {
        const response = await fetch(
          `${getApiBase()}/api/system-features/shopify`,
        );
        const data = await response.json();
        setShopifyEnabled(data.shopifyEnabled);
      } catch (error) {
        console.error("Error fetching Shopify status:", error);
        setShopifyEnabled(false); // Default to disabled on error (safer - won't show blank section)
      }
    };
    fetchShopifyStatus();
  }, []);

  return (
    <>
      <Header />

      {/*banner section*/}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "400px", md: "450px" },
          backgroundImage: `url(${homePageBanner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 1,
          }}
        ></Box>
        <Box
          className="banner-text"
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            color: "#fff",
          }}
        >
          {/*welcome to autoshops text*/}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: "#e63926",
              mb: 4,
              textShadow: "2px 2px 6px rgba(0,0,0,0.8)",
              fontSize: {
                xs: "3rem", // mobile
                md: "4rem", // desktops
              },
            }}
          >
            Autoshops.com
          </Typography>

          <Typography
            sx={{
              mt: 2,
              maxWidth: 600,
              textShadow: "2px 2px 6px rgba(0,0,0,0.8)",
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
            variant="h5"
          >
            Connecting Car Owners with Local Autoshops
          </Typography>

          {/*login/signup buttons on banner*/}
          <Box textAlign="center" my={3}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#4285f4",
                color: "#fff",
                mr: 2,
                "&:hover": { backgroundColor: "#1b449e" },
                borderRadius: 1,
                boxShadow: 4,
              }}
              onClick={() => navigate("/login")}
            >
              Login
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#4285f4",
                "&:hover": { backgroundColor: "#1b449e", color: "#fff" },
                borderRadius: 1,
                boxShadow: 4,
              }}
              onClick={() => setRoleDialogOpen(true)}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </Box>

      <paper
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          display: "flex",
          flexDirection: "row",
          gap: 4,
          p: 3,
          bgcolor: "#f8f9fb",
          mb: 4,
        }}
      >
        <Box
          sx={{
            mx: "auto",
            width: { xs: "100%", md: "70%" },
            my: 8,
            px: 2,
            textAlign: "center",
            fontSize: "1.5rem",
            lineHeight: 1.6,
          }}
        >
          The easiest way to get your car serviced. Find nearby shops, compare
          real-time quotes, and book with confidence, all in one place.
        </Box>
      </paper>

      {/*Expert mechanics card*/}
      <Box
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          my: 6,
          px: 2,
        }}
      >
        {/* Desktop layout: stacked vertically and full width */}
        {[
          {
            img: mechanicGraphic,
            title: "Autoshop Matching",
            desc: "Find the right shop for your car’s needs. Our smart matching system connects you with local autoshops based on your service, location, and availability.",
          },
          {
            img: biddingGraphic,
            title: "Easy as 1-2-3",
            desc: "1. Submit your service request in seconds by describing your issue and selecting your availability. \n\n2. Nearby autoshops send real-time quotes with pricing and timing, so you can easily compare your options.  \n\n3. Choose the best quote and book your service, all in one seamless experience with live updates.",
          },
        ].map((item, index) => (
          <Paper
            key={index}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 4,
              p: 3,
              bgcolor: "#f8f9fb",
              borderRadius: 3,
              boxShadow: 2,
              mb: 4,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box sx={{ display: { xs: "none", md: "flex" }, flex: 1 }}>
                <img
                  src={item.img}
                  alt={item.title}
                  style={{
                    width: "250px",
                    height: "auto",
                    maxHeight: "250px",
                  }}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "#e63926",
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.5rem",
                    color: "#444",
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Collapsible User Paths */}
      <Box
        className="signup-options"
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          my: 6,
          px: 2,
          display: "flex",
        }}
      >
        <Box className="signup-column" sx={{ flex: 1 }}>
          <Accordion
            className="signup-accordion"
            sx={{
              borderRadius: 6,
              overflow: "hidden",
              boxShadow: 3,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="h5"
                fontWeight={800}
                align="center"
                sx={{
                  color: "#e63926",
                  fontFamily: "Poppins",
                  textTransform: "uppercase",
                }}
              >
                I'm an Autoshop
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Receive service leads</strong> from local customers
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Respond to service requests</strong> to grow your
                    business
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Manage bookings</strong> in one dashboard
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Stand out</strong> in our local
                    network
                  </Typography>
                </Box>
                <Box textAlign="center" mt={3}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/signup?role=mechanic")}
                    className="signup-button owner"
                  >
                    Sign Up as Autoshop
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box className="signup-column" sx={{ flex: 1 }}>
          <Accordion
            className="signup-accordion"
            sx={{
              borderRadius: 6,
              overflow: "hidden",
              boxShadow: 3,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="h5"
                fontWeight={800}
                align="center"
                sx={{
                  color: "#e63926",
                  fontFamily: "Poppins",
                  textTransform: "uppercase",
                }}
              >
                I'm a Car Owner
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Get quotes and compare prices</strong> from local
                    shops
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Book services online</strong> with stress-free
                    scheduling
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Track your car's history</strong> in one place
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ color: "#e63926", mr: 1 }} />
                  <Typography variant="body1" sx={{ fontSize: "1.25rem" }}>
                    <strong>Get matching shops only</strong> - no more guessing
                  </Typography>
                </Box>
                <Box textAlign="center" mt={3}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/signup?role=User")}
                    className="signup-button owner"
                  >
                    Sign Up as Car Owner
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      {/*
      {/*Divider before team /}
      <Box
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={dividerImg}
          alt="Section divider"
          style={{
            width: "80%",
            height: "auto",
            maxWidth: "1600px",
            display: "block",
          }}
        />
      </Box>

      {/* Testimonials Section /}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          textAlign="center"
          sx={{ color: "#e63926", mb: 4 }}
        >
          What Our Customers Are Saying
        </Typography>

        <Grid container spacing={4} justifyContent="center" mb={4}>
          <Grid item xs={12} md={4}>
            <TestimonialCard
              name="Sarah J."
              role="Car Owner"
              quote="I found a great mechanic in minutes and saved over $200. So easy to use!"
              color="#4285f4"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TestimonialCard
              name="Tony M."
              role="Mechanic"
              quote="AutoShops helped me grow my client base faster than any other platform."
              color="#ffcb0f"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TestimonialCard
              name="Alex R."
              role="Car Owner"
              quote="The transparent pricing and easy booking process made car repairs stress-free."
              color="#e63926"
            />
          </Grid>
        </Grid>
      </Box>*/}
      {/* Shop Collection - Removed duplicate title, styling handled in component */}
      {shopifyEnabled && <ShopifyCollection />}

      {/* Review Ticker - Moved to bottom per Adam's request */}
      <ReviewTicker />

      {/* Role Selection Dialog */}
      <RoleSelectionDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
      />

      <Footer />
    </>
  );
};

export default Home;
